import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { DefinitionsCatalogBody } from '../models/catalog.types'
import type { ReportColumn } from '../models/column.types'
import { normalizeDefinitions } from '../service/columns/normalizeDefinitions'

export type MetricCatalogStatus = 'idle' | 'ready' | 'degraded'

/** Module 08's global metric/dimension catalog (contract 08 §6.1/§7). Deliberately NOT
 *  pinia-persisted — the §6.1 localStorage cache is the single persisted copy; the store
 *  hydrates from it via `primeMetricCatalog()`. */
export const useMetricCatalogStore = defineStore('metricCatalog', () => {
  const metrics = ref<Record<string, ReportColumn>>({})
  const dimensions = ref<Record<string, ReportColumn>>({})
  const runtimeMetrics = ref<Record<string, ReportColumn>>({})
  const status = ref<MetricCatalogStatus>('idle')

  const columns = computed<Record<string, ReportColumn>>(() => ({
    ...dimensions.value,
    ...metrics.value,
    ...runtimeMetrics.value,
  }))
  const currencyMetrics = computed<ReportColumn[]>(() =>
    Object.values({ ...metrics.value, ...runtimeMetrics.value }).filter(
      (column) => column.sub_type === 'currency',
    ),
  )

  function getColumn(field: string): ReportColumn | null {
    return columns.value[field] ?? null
  }

  function setCatalog(body: DefinitionsCatalogBody): void {
    const normalized = normalizeDefinitions(body)
    metrics.value = normalized.metrics
    dimensions.value = normalized.dimensions
    status.value = 'ready'
  }

  /** Session-local workspace definition overlay. The server catalog remains the baseline. */
  function upsertRuntimeMetric(column: ReportColumn): void {
    runtimeMetrics.value = {
      ...runtimeMetrics.value,
      [column.field]: { ...column, numeric: true },
    }
  }

  function removeRuntimeMetric(field: string): void {
    if (!(field in runtimeMetrics.value)) return
    const next = { ...runtimeMetrics.value }
    delete next[field]
    runtimeMetrics.value = next
  }

  /** A failed refresh only degrades when nothing usable is loaded (08 §12 fallback rules). */
  function markDegraded(): void {
    if (status.value !== 'ready') status.value = 'degraded'
  }

  function resetState(): void {
    metrics.value = {}
    dimensions.value = {}
    runtimeMetrics.value = {}
    status.value = 'idle'
  }

  return {
    metrics,
    dimensions,
    runtimeMetrics,
    status,
    columns,
    currencyMetrics,
    getColumn,
    setCatalog,
    upsertRuntimeMetric,
    removeRuntimeMetric,
    markDegraded,
    resetState,
  }
})

export type MetricCatalogStore = ReturnType<typeof useMetricCatalogStore>
